import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { brandConfig } from '../design/theme';

// =============================================================================
// OfficeHeroStage
// -----------------------------------------------------------------------------
// The hero animation: one PowerPoint window centre stage with the Excel and
// Word windows tucked behind it, all three working the same Nordic market
// sizing on a synchronised 15s loop. Lifted verbatim out of HomePage so the
// landing page and /ai-transformation render the SAME stage rather than two
// copies that drift apart.
//
// It is presentation only: no state, no props, and every class it uses lives
// in `landingHeroStyles` (HomePage.styles.js), which the host page must
// inject. Decorative throughout, so the host labels it for assistive
// technology.
// =============================================================================

export function OfficeHeroStage() {
  const theme = useTheme();

  return (
    <>
        {/* Centered PowerPoint Mockup — full size, pushed down below the
            headline / subhead / CTA so the title carries the top of the
            fold and the animation eases in lower (it can extend past the
            first fold). More vertical breathing room = cleaner hero. */}
        <Box
          className="landing-hero__pptx-mockup-wrapper"
          sx={{
            // Pulled up (was 10vh) so the whole animation block sits ~10%
            // higher and the hero reads cleaner, with less dead space
            // between the CTA row and the mockup.
            // The animation (and the floating artefacts, below) sit lower than
            // the title/subhead/CTA: only this block is pushed down, the text
            // above stays put.
            '&.landing-hero__pptx-mockup-wrapper': {
              // lg+ gets extra headroom so the Excel/Word side windows have a
              // clear band above the PowerPoint frame to peek into (they are
              // lg-only); md and below keep the tighter PowerPoint-only gap.
              marginTop: { xs: '2rem', md: '116px', lg: '150px' },
            },
          }}
        >
          {/* Background surfaces: Excel (left) and Word (right, higher), tucked
              behind the PowerPoint window so the hero shows all three Office
              surfaces working the same Nordic market sizing together. Large
              screens only; behind the main mockup (zIndex 0); non-interactive. */}
          <Box
            aria-hidden="true"
            sx={{
              // All three surfaces on every platform: phone (xs) fans Excel +
              // Word below PowerPoint; tablet (sm/md) + desktop (lg) flank them
              // beside a shrunk-on-tablet PowerPoint frame.
              display: 'block',
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              pointerEvents: 'none',
              '@keyframes restackHeroSideFloat': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-6px)' },
              },
              // Loop-synced highlight: the Excel window pops green while the
              // chat panel's "Reading your workspace" beat reveals the [XLSX]
              // row (~14-19% of the 15s loop), then Word pops blue as the
              // [DOCX] row reveals (~18-23%). Shows the cross-app referencing.
              '@keyframes restackXlGlow': {
                '0%, 11%': { opacity: 0.9, boxShadow: '0 22px 50px rgba(46,20,10,0.18)', filter: 'saturate(0.94)' },
                '14%, 19%': { opacity: 1, boxShadow: '0 16px 40px rgba(46,20,10,0.16), 0 0 0 3px rgba(33,115,70,0.6), 0 12px 36px rgba(33,115,70,0.5)', filter: 'saturate(1.2) brightness(1.03)' },
                '24%, 100%': { opacity: 0.9, boxShadow: '0 22px 50px rgba(46,20,10,0.18)', filter: 'saturate(0.94)' },
              },
              '@keyframes restackWdGlow': {
                '0%, 15%': { opacity: 0.9, boxShadow: '0 22px 50px rgba(46,20,10,0.18)', filter: 'saturate(0.94)' },
                '18%, 23%': { opacity: 1, boxShadow: '0 16px 40px rgba(46,20,10,0.16), 0 0 0 3px rgba(53,84,138,0.62), 0 12px 36px rgba(53,84,138,0.5)', filter: 'saturate(1.2) brightness(1.03)' },
                '28%, 100%': { opacity: 0.9, boxShadow: '0 22px 50px rgba(46,20,10,0.18)', filter: 'saturate(0.94)' },
              },
            }}
          >
            {/* EXCEL — left, slightly lower. A real Excel window: green title
                bar, ribbon tabs, a formula bar, a gridded model (column
                letters + row numbers), a mini chart and sheet tabs. Ambient
                "work happening": the SOM cell value fills in + its selection
                pulses, the calc sliver sweeps, and the tallest bar grows. */}
            <Box sx={{ position: 'absolute', top: { xs: 30, sm: 55, lg: 60 }, left: '50%', transformOrigin: { xs: 'top', sm: 'center', lg: 'center' }, transform: { xs: 'translateX(-61%) rotate(-5deg) scale(0.3)', sm: 'translateX(-82%) rotate(-5deg) scale(0.62)', lg: 'translateX(-97%) rotate(-5deg)' }, width: 720 }}>
              <Box
                sx={{
                  animation: 'restackHeroSideFloat 7s ease-in-out infinite, restackXlGlow 15s ease infinite',
                  borderRadius: '14px', overflow: 'hidden', background: '#fff',
                  border: '1px solid rgba(46,20,10,0.08)', boxShadow: '0 22px 50px rgba(46,20,10,0.18)',
                  fontFamily: theme.typography.fontFamily,
                  '@keyframes rsXlCellPulse': { '0%, 100%': { boxShadow: '0 0 0 1.5px #217346 inset' }, '50%': { boxShadow: '0 0 0 2.5px #2A8C56 inset' } },
                  '@keyframes rsXlFillIn': { '0%, 22%': { opacity: 0, transform: 'translateY(3px)' }, '46%, 100%': { opacity: 1, transform: 'translateY(0)' } },
                  '@keyframes rsXlSweep': { '0%': { transform: 'translateX(-110%)' }, '100%': { transform: 'translateX(360%)' } },
                  '@keyframes rsXlBarGrow': { '0%': { transform: 'scaleY(0.32)' }, '55%, 100%': { transform: 'scaleY(1)' } },
                }}
              >
                {/* Title bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.9, background: '#217346' }}>
                  <Box sx={{ width: 16, height: 16, borderRadius: '3px', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.58rem', fontWeight: 800, color: '#fff' }}>X</Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#fff', fontFamily: theme.typography.fontFamily }}>Nordic_Market_Sizing.xlsx</Typography>
                  <Box component="span" sx={{ ml: 'auto', px: 0.8, py: 0.2, borderRadius: '999px', background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.07em' }}>EXCEL</Box>
                </Box>
                {/* Ribbon tabs */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, px: 1.5, pt: 0.7, borderBottom: '1px solid rgba(46,20,10,0.07)', background: 'rgba(33,115,70,0.04)' }}>
                  {['File', 'Home', 'Insert', 'Draw', 'Formulas', 'Data', 'Review', 'View'].map((t, i) => (
                    <Typography key={t} sx={{ fontSize: '0.56rem', fontWeight: i === 1 ? 700 : 500, color: i === 1 ? '#217346' : 'rgba(46,20,10,0.55)', pb: 0.5, borderBottom: i === 1 ? '2px solid #217346' : '2px solid transparent', fontFamily: theme.typography.fontFamily }}>{t}</Typography>
                  ))}
                  <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.3, mb: 0.4, px: 0.7, py: 0.2, borderRadius: '999px', background: '#217346', color: '#fff', fontSize: '0.5rem', fontWeight: 700 }}>
                    <brandConfig.icon sx={{ fontSize: '0.55rem' }} /> Open Restack
                  </Box>
                </Box>
                {/* Formula bar */}
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.55, borderBottom: '1px solid rgba(46,20,10,0.07)' }}>
                  <Box sx={{ px: 0.7, py: 0.15, minWidth: 30, textAlign: 'center', borderRadius: '4px', border: '1px solid rgba(46,20,10,0.14)', fontSize: '0.54rem', fontWeight: 600, color: 'rgba(46,20,10,0.65)', fontFamily: theme.typography.fontFamily }}>C5</Box>
                  <Typography sx={{ fontStyle: 'italic', fontSize: '0.6rem', color: 'rgba(46,20,10,0.4)' }}>fx</Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: 'rgba(46,20,10,0.7)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>=SUMPRODUCT(Adoption, ARPU)</Typography>
                  <Box sx={{ position: 'absolute', left: 0, bottom: 0, height: 2, width: '28%', background: 'linear-gradient(90deg, transparent, #2A8C56, transparent)', animation: 'rsXlSweep 3.4s ease-in-out infinite' }} />
                </Box>
                {/* Grid */}
                <Box sx={{ p: 1.3 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '20px 62px 80px 0.7fr 1fr', border: '1px solid rgba(46,20,10,0.09)', borderRadius: '4px', overflow: 'hidden', fontFamily: theme.typography.fontFamily }}>
                    {['', 'A', 'B', 'C', 'D'].map((c, i) => (
                      <Box key={`ch${i}`} sx={{ py: 0.3, textAlign: 'center', fontSize: '0.5rem', fontWeight: 600, color: 'rgba(46,20,10,0.42)', background: 'rgba(46,20,10,0.04)', borderBottom: '1px solid rgba(46,20,10,0.08)', borderRight: i < 4 ? '1px solid rgba(46,20,10,0.05)' : 'none' }}>{c}</Box>
                    ))}
                    {[
                      ['1', 'Segment', '2026E', 'CAGR', 'Source'],
                      ['2', 'TAM', '$4.2B', '41%', 'SEC, IR'],
                      ['3', 'SAM', '$1.8B', '38%', 'PitchBook'],
                      ['4', 'SOM', '$340M', '41%', 'model'],
                      ['5', 'Capture', '4.7%', '', 'assumption'],
                    ].flatMap((row, r) => row.map((v, c) => ({ v, r, c }))).map(({ v, r, c }) => {
                      const isRowNum = c === 0;
                      const isHeader = r === 0;
                      const isSel = r === 3 && c === 2; // SOM, $340M
                      const isNum = (c === 2 || c === 3) && !isHeader;
                      return (
                        <Box
                          key={`${r}-${c}`}
                          sx={{
                            position: 'relative',
                            px: isRowNum ? 0 : 0.7, py: 0.34,
                            fontSize: '0.55rem',
                            fontWeight: isHeader ? 700 : (c === 1 ? 600 : 500),
                            textAlign: isRowNum ? 'center' : (isNum ? 'right' : 'left'),
                            color: isRowNum ? 'rgba(46,20,10,0.4)' : isHeader ? 'rgba(46,20,10,0.75)' : isNum ? '#217346' : 'rgba(46,20,10,0.7)',
                            background: isRowNum ? 'rgba(46,20,10,0.04)' : isHeader ? 'rgba(33,115,70,0.07)' : '#fff',
                            borderRight: c < 4 ? '1px solid rgba(46,20,10,0.05)' : 'none',
                            borderBottom: r < 4 ? '1px solid rgba(46,20,10,0.05)' : 'none',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            ...(isSel ? { animation: 'rsXlCellPulse 2.8s ease-in-out infinite', zIndex: 1 } : {}),
                          }}
                        >
                          {isSel ? <Box component="span" sx={{ display: 'inline-block', animation: 'rsXlFillIn 4.8s ease-in-out infinite' }}>{v}</Box> : v}
                        </Box>
                      );
                    })}
                  </Box>
                  <Box sx={{ mt: 1.1, display: 'flex', alignItems: 'flex-end', gap: 0.7, height: 40 }}>
                    {[46, 64, 82, 100].map((h, i) => (
                      <Box key={i} sx={{ flex: 1, height: `${h}%`, transformOrigin: 'bottom', borderRadius: '3px 3px 0 0', background: i === 3 ? '#217346' : 'rgba(33,115,70,0.4)', animation: `rsXlBarGrow 4.8s ease-in-out ${(i * 0.18).toFixed(2)}s infinite` }} />
                    ))}
                  </Box>
                  <Typography sx={{ mt: 0.7, fontSize: '0.52rem', color: 'rgba(46,20,10,0.45)', fontFamily: theme.typography.fontFamily }}>Bottom-up model &middot; formula-driven</Typography>
                </Box>
                {/* Sheet tabs */}
                <Box sx={{ display: 'flex', alignItems: 'center', borderTop: '1px solid rgba(46,20,10,0.07)', background: 'rgba(46,20,10,0.025)', px: 1, pt: 0.4 }}>
                  {['Model', 'Assumptions', 'Sources'].map((t, i) => (
                    <Typography key={t} sx={{ px: 0.9, py: 0.35, fontSize: '0.52rem', fontWeight: i === 0 ? 700 : 500, color: i === 0 ? '#217346' : 'rgba(46,20,10,0.5)', borderBottom: i === 0 ? '2px solid #217346' : '2px solid transparent', background: i === 0 ? '#fff' : 'transparent', fontFamily: theme.typography.fontFamily }}>{t}</Typography>
                  ))}
                  <Typography sx={{ px: 0.7, fontSize: '0.62rem', color: 'rgba(46,20,10,0.35)' }}>+</Typography>
                </Box>
              </Box>
            </Box>

            {/* WORD — right, slightly higher. A real Word window: blue title
                bar, ribbon tabs with Review active, a Track Changes chip, a
                white page on a canvas, and a tracked-change redline that
                "lands" on a loop with a blinking caret and a margin change bar. */}
            <Box sx={{ position: 'absolute', top: { xs: -30, sm: -28, lg: -82 }, left: '50%', transformOrigin: { xs: 'top', sm: 'center', lg: 'center' }, transform: { xs: 'translateX(-28%) rotate(5deg) scale(0.3)', sm: 'translateX(9%) rotate(5deg) scale(0.62)', lg: 'translateX(37%) rotate(5deg)' }, width: 520 }}>
              <Box
                sx={{
                  animation: 'restackHeroSideFloat 8s ease-in-out infinite 0.6s, restackWdGlow 15s ease infinite',
                  borderRadius: '14px', overflow: 'hidden', background: '#fff',
                  border: '1px solid rgba(46,20,10,0.08)', boxShadow: '0 22px 50px rgba(46,20,10,0.18)',
                  fontFamily: theme.typography.fontFamily,
                  '@keyframes rsWdCaret': { '0%, 45%': { opacity: 1 }, '55%, 100%': { opacity: 0 } },
                  '@keyframes rsWdInsIn': { '0%, 28%': { opacity: 0 }, '48%, 92%': { opacity: 1 }, '100%': { opacity: 0 } },
                  '@keyframes rsWdBar': { '0%, 28%': { opacity: 0 }, '48%, 92%': { opacity: 1 }, '100%': { opacity: 0 } },
                }}
              >
                {/* Title bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 0.55, background: '#35548A' }}>
                  <Box sx={{ width: 16, height: 16, borderRadius: '3px', background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.58rem', fontWeight: 800, color: '#fff' }}>W</Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#fff', fontFamily: theme.typography.fontFamily }}>Nordic_Market_Sizing.docx</Typography>
                  <Box component="span" sx={{ ml: 'auto', px: 0.8, py: 0.2, borderRadius: '999px', background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.07em' }}>WORD</Box>
                </Box>
                {/* Ribbon tabs (Review active) with a Track Changes chip + Restack pill */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, px: 1.5, pt: 0.45, borderBottom: '1px solid rgba(46,20,10,0.07)', background: 'rgba(53,84,138,0.04)' }}>
                  {['File', 'Home', 'Insert', 'References', 'Review', 'View'].map((t) => {
                    const active = t === 'Review';
                    return <Typography key={t} sx={{ fontSize: '0.56rem', fontWeight: active ? 700 : 500, color: active ? '#35548A' : 'rgba(46,20,10,0.55)', pb: 0.5, borderBottom: active ? '2px solid #35548A' : '2px solid transparent', fontFamily: theme.typography.fontFamily }}>{t}</Typography>;
                  })}
                  <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.35, mb: 0.4, px: 0.6, py: 0.18, borderRadius: '999px', background: 'rgba(53,84,138,0.12)', color: '#35548A', fontSize: '0.46rem', fontWeight: 700, letterSpacing: '0.04em' }}>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: '#35548A' }} /> TRACK CHANGES
                  </Box>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mb: 0.4, px: 0.65, py: 0.18, borderRadius: '999px', background: '#35548A', color: '#fff', fontSize: '0.48rem', fontWeight: 700 }}>
                    <brandConfig.icon sx={{ fontSize: '0.5rem' }} /> Open Restack
                  </Box>
                </Box>
                {/* Document page on a canvas */}
                <Box sx={{ p: 0.45, background: 'rgba(46,20,10,0.035)' }}>
                  <Box sx={{ background: '#fff', borderRadius: '3px', border: '1px solid rgba(46,20,10,0.06)', boxShadow: '0 1px 4px rgba(46,20,10,0.05)', px: 1.5, py: 0.6 }}>
                    <Box sx={{ position: 'relative', pl: 1, mb: 1.1 }}>
                      <Box sx={{ position: 'absolute', left: -5, top: 3, bottom: 3, width: 2, borderRadius: '2px', background: '#35548A', animation: 'rsWdBar 6.6s ease-in-out infinite' }} />
                      <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '0.66rem', lineHeight: 1.7, color: 'rgba(46,20,10,0.82)' }}>
                        The Nordic payments market reaches{' '}
                        <Box component="span" sx={{ color: '#C0392B', textDecoration: 'line-through' }}>$3.4B</Box>{' '}
                        <Box component="span" sx={{ color: '#2B579A', fontWeight: 700, textDecoration: 'underline', animation: 'rsWdInsIn 6.6s ease-in-out infinite' }}>$4.2B by 2026</Box>
                        <Box component="span" sx={{ display: 'inline-block', width: '1.5px', height: '0.78em', ml: '1.5px', verticalAlign: '-1px', background: '#2B579A', animation: 'rsWdCaret 1.1s step-end infinite' }} />
                        .
                      </Typography>
                    </Box>
                    <Box sx={{ height: 6, borderRadius: '3px', background: 'rgba(46,20,10,0.08)', mb: 0.7, width: '97%' }} />
                    <Box sx={{ height: 6, borderRadius: '3px', background: 'rgba(46,20,10,0.08)', mb: 0.7, width: '88%' }} />
                    <Box sx={{ height: 6, borderRadius: '3px', background: 'rgba(46,20,10,0.08)', width: '58%' }} />
                  </Box>
                </Box>
                {/* Status strip */}
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, borderTop: '1px solid rgba(46,20,10,0.07)', background: 'rgba(53,84,138,0.04)' }}>
                  <Typography sx={{ fontSize: '0.5rem', color: 'rgba(46,20,10,0.5)', fontFamily: theme.typography.fontFamily }}>Page 1 of 3 &middot; 2,140 words &middot; English (US)</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className="landing-hero__pptx-mockup">
            {/* Title bar */}
            <Box className="landing-hero__pptx-titlebar">
              <Box className="landing-hero__pptx-titlebar-left">
                <Box className="landing-hero__pptx-titlebar-dots">
                  <span className="landing-hero__pptx-dot landing-hero__pptx-dot--red" />
                  <span className="landing-hero__pptx-dot landing-hero__pptx-dot--yellow" />
                  <span className="landing-hero__pptx-dot landing-hero__pptx-dot--green" />
                </Box>
                <Box className="landing-hero__pptx-titlebar-quick">
                  <span className="landing-hero__pptx-quick-btn" />
                  <span className="landing-hero__pptx-quick-btn" />
                  <span className="landing-hero__pptx-quick-btn" />
                </Box>
              </Box>
              <Box className="landing-hero__pptx-titlebar-center">
                <svg className="landing-hero__pptx-titlebar-icon" viewBox="0 0 48 48" width="14" height="14" aria-label="PowerPoint">
                  <rect x="24" y="4" width="20" height="40" rx="2" fill="#ED6C47"/>
                  <rect x="28" y="12" width="12" height="2" rx="1" fill="#FF8F6B"/>
                  <rect x="28" y="18" width="12" height="2" rx="1" fill="#FF8F6B"/>
                  <rect x="28" y="24" width="12" height="2" rx="1" fill="#FF8F6B"/>
                  <rect x="4" y="8" width="28" height="32" rx="3" fill="#B83B1D"/>
                  <path d="M13 17h7c3 0 5.5 2 5.5 5s-2.5 5-5.5 5h-4v6h-3V17zm3 7h4c1.5 0 2.5-1 2.5-2s-1-2-2.5-2h-4v4z" fill="#fff"/>
                </svg>
                <Typography className="landing-hero__pptx-titlebar-text">
                  Nordic_Market_Sizing.pptx
                </Typography>
              </Box>
              <Box className="landing-hero__pptx-titlebar-right">
                <Box className="landing-hero__pptx-search-box">
                  <span className="landing-hero__pptx-search-icon">&#128269;</span>
                  <span className="landing-hero__pptx-search-text">Search</span>
                </Box>
                <Box className="landing-hero__pptx-share-btn">Share</Box>
              </Box>
            </Box>
            {/* Ribbon */}
            <Box className="landing-hero__pptx-ribbon">
              <span className="landing-hero__pptx-ribbon-tab">File</span>
              <span className="landing-hero__pptx-ribbon-tab landing-hero__pptx-ribbon-tab--home landing-hero__pptx-ribbon-tab--active">Home</span>
              <span className="landing-hero__pptx-ribbon-tab">Insert</span>
              <span className="landing-hero__pptx-ribbon-tab">Draw</span>
              <span className="landing-hero__pptx-ribbon-tab">Design</span>
              <span className="landing-hero__pptx-ribbon-tab">Transitions</span>
              <span className="landing-hero__pptx-ribbon-tab">Animations</span>
              <span className="landing-hero__pptx-ribbon-tab">Slide Show</span>
              <span className="landing-hero__pptx-ribbon-tab">Record</span>
              <span className="landing-hero__pptx-ribbon-tab">Review</span>
              <span className="landing-hero__pptx-ribbon-tab">View</span>
              <span className="landing-hero__pptx-ribbon-tab landing-hero__pptx-ribbon-tab--restack">
                <brandConfig.icon sx={{ fontSize: '0.6rem' }} />
                Open Restack
              </span>
            </Box>
            {/* Ribbon Toolbar */}
            <Box className="landing-hero__pptx-toolbar">
              <Box className="landing-hero__pptx-toolbar-group">
                <span className="landing-hero__pptx-toolbar-btn" />
                <span className="landing-hero__pptx-toolbar-btn" />
                <span className="landing-hero__pptx-toolbar-btn landing-hero__pptx-toolbar-btn--wide" />
              </Box>
              <span className="landing-hero__pptx-toolbar-sep" />
              <Box className="landing-hero__pptx-toolbar-group">
                <span className="landing-hero__pptx-toolbar-btn" />
                <span className="landing-hero__pptx-toolbar-btn" />
                <span className="landing-hero__pptx-toolbar-btn" />
              </Box>
              <span className="landing-hero__pptx-toolbar-sep" />
              <Box className="landing-hero__pptx-toolbar-group">
                <span className="landing-hero__pptx-toolbar-btn landing-hero__pptx-toolbar-btn--wide" />
                <span className="landing-hero__pptx-toolbar-btn" />
                <span className="landing-hero__pptx-toolbar-btn" />
              </Box>
              <span className="landing-hero__pptx-toolbar-sep" />
              <Box className="landing-hero__pptx-toolbar-group">
                <span className="landing-hero__pptx-toolbar-btn" />
                <span className="landing-hero__pptx-toolbar-btn landing-hero__pptx-toolbar-btn--wide" />
              </Box>
            </Box>
            {/* Body */}
            <Box className="landing-hero__pptx-body">
              {/* Slide thumbnails — slide 4 is the permanent "active"
                  slide (where Restack is building). Slides 2, 7, 8 get
                  a transient rust highlight during the loop's "Reading
                  deck context" beat (24-34% of the 15s loop) so the
                  filmstrip visually corresponds to the chat trail's
                  "Slide 2: Executive Summary / Slide 7: Methodology /
                  Slide 8: Sources cited" rows. The highlights stagger
                  to match the bar-fill sequence in the detail panel. */}
              <Box className="landing-hero__pptx-thumbs">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => {
                  const isActive = n === 4;
                  const isReading = [2, 7, 8].includes(n);
                  const cls = [
                    'landing-hero__pptx-thumb',
                    isActive ? 'landing-hero__pptx-thumb--active' : '',
                    isReading ? `landing-hero__pptx-thumb--reading-${n}` : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <Box key={n} className={cls}>
                      <span className="landing-hero__pptx-thumb-num">{n}</span>
                      <Box className="landing-hero__pptx-thumb-slide" />
                    </Box>
                  );
                })}
              </Box>
              {/* Slide area */}
              <Box className="landing-hero__pptx-slide-area">
                <Box className="landing-hero__pptx-slide">
                  {/* Action title (McKinsey/BCG-style "answer-first" headline)
                      — states the slide's argument, not the topic. An exec
                      reading only the title row should still walk away with
                      the takeaway. The bullets + chart + insight below are
                      the supporting evidence for THIS claim. */}
                  <Box className="pptx-slide__title">Nordic entry reaches $340M by 2029, scaling at 41% CAGR - 2x EU growth</Box>
                  <Box className="pptx-slide__subtitle">Payment Processing &amp; Digital Banking | Q4 2026 Analysis</Box>
                  <Box className="pptx-slide__divider" />
                  {/* Two-exhibit body, the standard consulting market-sizing
                      layout: the sizing funnel on the left establishes the
                      opportunity, the projection on the right carries it
                      forward. Each exhibit is numbered and unit-labelled the
                      way a committee page is, so the reader can cite a figure
                      by exhibit rather than by pointing. */}
                  <Box className="pptx-slide__exhibits">
                    <Box className="pptx-slide__exhibit">
                      <Box className="pptx-slide__exhibit-head pptx-slide__exhibit-frame--1">
                        <span className="pptx-slide__exhibit-num">Exhibit 1</span>
                        <span className="pptx-slide__exhibit-title">Market sizing ($B)</span>
                      </Box>
                      <Box className="pptx-slide__funnel">
                        {/* Bar widths are the real proportions of the figures
                            (4.2 / 1.8 / 0.34), so the funnel narrows honestly. */}
                        <Box className="pptx-slide__funnel-row pptx-slide__bullet--1">
                          <span className="pptx-slide__funnel-label">TAM</span>
                          <span className="pptx-slide__funnel-track">
                            <span className="pptx-slide__funnel-bar pptx-slide__funnel-bar--tam" />
                          </span>
                          <span className="pptx-slide__funnel-value">
                            $4.2B<sup className="pptx-slide__cite">1</sup>
                          </span>
                        </Box>
                        <Box className="pptx-slide__funnel-row pptx-slide__bullet--2">
                          <span className="pptx-slide__funnel-label">SAM</span>
                          <span className="pptx-slide__funnel-track">
                            <span className="pptx-slide__funnel-bar pptx-slide__funnel-bar--sam" />
                          </span>
                          <span className="pptx-slide__funnel-value">
                            $1.8B<sup className="pptx-slide__cite">2</sup>
                          </span>
                        </Box>
                        <Box className="pptx-slide__funnel-row pptx-slide__bullet--3">
                          <span className="pptx-slide__funnel-label">SOM</span>
                          <span className="pptx-slide__funnel-track">
                            <span className="pptx-slide__funnel-bar pptx-slide__funnel-bar--som" />
                          </span>
                          <span className="pptx-slide__funnel-value">
                            $340M<sup className="pptx-slide__cite">3</sup>
                          </span>
                        </Box>
                      </Box>
                      <Box className="pptx-slide__exhibit-foot pptx-slide__bullet--3">
                        Serviceable segment is B2B SaaS-accessible; obtainable assumes 3.2% penetration by 2029
                      </Box>
                    </Box>

                    <Box className="pptx-slide__exhibit pptx-slide__chart-wrapper">
                      <Box className="pptx-slide__exhibit-head pptx-slide__exhibit-frame--2">
                        <span className="pptx-slide__exhibit-num">Exhibit 2</span>
                        <span className="pptx-slide__exhibit-title">Revenue projection ($M)</span>
                        <span className="pptx-slide__chart-cagr">CAGR 41%</span>
                      </Box>
                      <Box className="pptx-slide__chart pptx-slide__exhibit-frame--2">
                        <Box className="pptx-slide__chart-bar pptx-slide__chart-bar--1">
                          <span className="pptx-slide__bar-value">$120M</span>
                          <span className="pptx-slide__chart-label">2026</span>
                        </Box>
                        <Box className="pptx-slide__chart-bar pptx-slide__chart-bar--2">
                          <span className="pptx-slide__bar-value">$185M</span>
                          <span className="pptx-slide__chart-label">2027</span>
                        </Box>
                        <Box className="pptx-slide__chart-bar pptx-slide__chart-bar--3">
                          <span className="pptx-slide__bar-value">$260M</span>
                          <span className="pptx-slide__chart-label">2028</span>
                        </Box>
                        <Box className="pptx-slide__chart-bar pptx-slide__chart-bar--4">
                          <span className="pptx-slide__bar-value">$340M</span>
                          <span className="pptx-slide__chart-label">2029</span>
                        </Box>
                      </Box>
                      <Box className="pptx-slide__exhibit-foot pptx-slide__exhibit-frame--2">
                        Bottom-up build; 2026 base actualised, 2027 onward projected
                      </Box>
                      {/* Selection box — wraps the projection exhibit, proving
                          the chart is a real PPT object (not a flat image).
                          Auto-follows heroChartMove translate during the drag. */}
                      <Box className="pptx-slide__edit-select">
                        <span className="pptx-slide__edit-handle" style={{top:-2,left:-2}} />
                        <span className="pptx-slide__edit-handle" style={{top:-2,right:-2}} />
                        <span className="pptx-slide__edit-handle" style={{bottom:-2,left:-2}} />
                        <span className="pptx-slide__edit-handle" style={{bottom:-2,right:-2}} />
                        <span className="pptx-slide__edit-handle" style={{top:-2,left:'calc(50% - 2px)'}} />
                        <span className="pptx-slide__edit-handle" style={{bottom:-2,left:'calc(50% - 2px)'}} />
                        <span className="pptx-slide__edit-handle" style={{top:'calc(50% - 2px)',left:-2}} />
                        <span className="pptx-slide__edit-handle" style={{top:'calc(50% - 2px)',right:-2}} />
                      </Box>
                    </Box>
                  </Box>
                  <Box className="pptx-slide__insight">
                    <span className="pptx-slide__insight-label">Key Insight:</span> Nordic payment volume growing
                    <span className="pptx-slide__insight-figure">
                      &nbsp;23% YoY<sup className="pptx-slide__cite">4</sup>
                    </span>
                    , 2x EU average
                  </Box>
                  {/* Numbered source list — single horizontal row to keep
                      the slide at its native 16:9 proportion. Each item is a
                      superscript-numbered faux-link (underline + accent on
                      hover) reading like a proper consulting footnote bar. */}
                  <Box className="pptx-slide__sources">
                    <Box className="pptx-slide__source-row pptx-slide__source-row--1">
                      <span className="pptx-slide__source-num">1</span>
                      <a className="pptx-slide__source-link" href="#" onClick={(e) => e.preventDefault()}>
                        Nordic Capital Markets 2026
                      </a>
                    </Box>
                    <Box className="pptx-slide__source-row pptx-slide__source-row--2">
                      <span className="pptx-slide__source-num">2</span>
                      <a className="pptx-slide__source-link" href="#" onClick={(e) => e.preventDefault()}>
                        ECB Fintech Q3 2026
                      </a>
                    </Box>
                    <Box className="pptx-slide__source-row pptx-slide__source-row--3">
                      <span className="pptx-slide__source-num">3</span>
                      <a className="pptx-slide__source-link" href="#" onClick={(e) => e.preventDefault()}>
                        Dealroom Nordic
                      </a>
                    </Box>
                    <Box className="pptx-slide__source-row pptx-slide__source-row--4">
                      <span className="pptx-slide__source-num">4</span>
                      <a className="pptx-slide__source-link" href="#" onClick={(e) => e.preventDefault()}>
                        Statista Payments 2026
                      </a>
                    </Box>
                  </Box>
                  <Box className="pptx-slide__methodology">
                    Methodology: Bottom-up. Fintech user base &times; ARPU &times; penetration curve
                  </Box>
                  <Box className="pptx-slide__page-num">4</Box>
                  {/* Cursor — positioned inside slide for coord math */}
                  <Box className="pptx-slide__edit-cursor">
                    <svg width="14" height="20" viewBox="0 0 320 512" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0 55.2V426c0 12.2 9.9 22 22 22c6.3 0 12.4-2.7 16.6-7.5L121.2 346l58.1 116.3c7.9 15.8 27.1 22.2 42.9 14.3s22.2-27.1 14.3-42.9L179.8 320H297.9c12.1 0 21.7-10 22-22.1c.3-13.1-5.9-23.7-17.3-33.7L22.6 6.4C18.1 2.4 12.4 0 6.6 0C2.4 0 0 3.2 0 6.6V55.2z" fill="white" stroke="#1a1a1a" strokeWidth="24"/>
                    </svg>
                  </Box>
                </Box>
              </Box>
              {/* The Restack panel. */}
              <Box className="landing-hero__pptx-sidebar">
                <Box className="landing-hero__pptx-sidebar-header">
                  <brandConfig.icon sx={{ fontSize: '0.7rem', color: '#B83B1D' }} />
                  Restack AI
                </Box>
                <Box className="pptx-sidebar__msg pptx-sidebar__msg--user">
                  {/* Order: prompt text first (the user's actual ask),
                      then attachments below as supporting evidence. Reads
                      like a real chat composer — message body up top,
                      file chips beneath where they were dragged in. */}
                  <Box className="pptx-sidebar__msg-text">
                    Build a market sizing slide for Nordic fintech entry. Pull the numbers from my Excel model and the thesis from Word.
                  </Box>
                  <Box className="pptx-sidebar__msg-attachments">
                    <Box className="pptx-sidebar__attachment pptx-sidebar__attachment--pdf">
                      <span className="pptx-sidebar__attachment-badge">PDF</span>
                      <span className="pptx-sidebar__attachment-name">Nordic_Brief.pdf</span>
                    </Box>
                  </Box>
                </Box>
                {/* Thinking dots removed — animation starts directly with Step 1 */}
                {/* Steps container — flex column that GROWS as each step
                    appears. Each step is a self-contained card whose row
                    morphs from "● Active label..." (with detail panel
                    expanded below) to "✓ Done label" (single line) once
                    that step's window finishes. The next active step then
                    appears beneath the previous done line, producing a
                    sequential top-down progress trail. */}
                <Box className="pptx-sidebar__steps-container">
                  {/* Step 1 — Reading the user's workspace (the connector beat).
                      The deck pulls live from the Excel model (green) and the
                      Word thesis (blue), plus prior slides in this same deck.
                      Reuses the source-item reveal list + its 14-22% timing. */}
                  <Box className="pptx-sidebar__step pptx-sidebar__step--1">
                    <Box className="pptx-sidebar__step-row">
                      <Box className="pptx-sidebar__step-icon-stack">
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--spinner" />
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--check">&#10003;</span>
                      </Box>
                      <Box className="pptx-sidebar__step-label-stack">
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--active">Reading your workspace...</span>
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--done">Read Excel, Word + deck</span>
                      </Box>
                    </Box>
                    <Box className="pptx-sidebar__step-detail">
                      <Box className="pptx-sidebar__source-item pptx-sidebar__source-item--1">
                        <span className="pptx-sidebar__source-tag" style={{ color: '#1B7A4F', background: 'rgba(33,115,70,0.14)' }}>[XLSX]</span>Nordic_Market_Sizing.xlsx
                      </Box>
                      <Box className="pptx-sidebar__source-item pptx-sidebar__source-item--2">
                        <span className="pptx-sidebar__source-tag" style={{ color: '#2B579A', background: 'rgba(53,84,138,0.14)' }}>[DOCX]</span>Nordic_Market_Sizing.docx
                      </Box>
                      <Box className="pptx-sidebar__source-item pptx-sidebar__source-item--3">
                        <span className="pptx-sidebar__source-tag">[DECK]</span>Slide 2 &middot; Executive Summary
                      </Box>
                      <Box className="pptx-sidebar__source-item pptx-sidebar__source-item--4">
                        <span className="pptx-sidebar__source-tag">[DECK]</span>Slide 7 &middot; Methodology
                      </Box>
                      <Box className="pptx-sidebar__source-item pptx-sidebar__source-item--5">
                        <span className="pptx-sidebar__source-tag">[DECK]</span>Slide 8 &middot; Sources cited
                      </Box>
                    </Box>
                  </Box>

                  {/* Step 2 — Scanning external sources. Comes after the
                      workspace read so the flow is: pull from your own files
                      first, then augment with outside research. Reuses the
                      analysis-row bars (24-33% timing) as per-source scan
                      progress; the third row tallies up to 87. */}
                  <Box className="pptx-sidebar__step pptx-sidebar__step--2">
                    <Box className="pptx-sidebar__step-row">
                      <Box className="pptx-sidebar__step-icon-stack">
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--spinner" />
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--check">&#10003;</span>
                      </Box>
                      <Box className="pptx-sidebar__step-label-stack">
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--active">Scanning 87 sources...</span>
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--done">Scanned 87 sources</span>
                      </Box>
                    </Box>
                    <Box className="pptx-sidebar__step-detail">
                      <Box className="pptx-sidebar__analysis-row">
                        <Box className="pptx-sidebar__analysis-header">
                          <span className="pptx-sidebar__analysis-label">Nordic Capital Markets 2026</span>
                          <span className="pptx-sidebar__analysis-value pptx-sidebar__analysis-value--1">&#10003;</span>
                        </Box>
                        <Box className="pptx-sidebar__analysis-bar pptx-sidebar__analysis-bar--1">
                          <Box className="pptx-sidebar__analysis-fill" />
                        </Box>
                      </Box>
                      <Box className="pptx-sidebar__analysis-row">
                        <Box className="pptx-sidebar__analysis-header">
                          <span className="pptx-sidebar__analysis-label">ECB Fintech Report Q3</span>
                          <span className="pptx-sidebar__analysis-value pptx-sidebar__analysis-value--2">&#10003;</span>
                        </Box>
                        <Box className="pptx-sidebar__analysis-bar pptx-sidebar__analysis-bar--2">
                          <Box className="pptx-sidebar__analysis-fill" />
                        </Box>
                      </Box>
                      <Box className="pptx-sidebar__analysis-row">
                        <Box className="pptx-sidebar__analysis-header">
                          <span className="pptx-sidebar__analysis-label">Klarna, Statista + 85 more</span>
                          <span className="pptx-sidebar__analysis-value pptx-sidebar__analysis-value--3">&#10003;</span>
                        </Box>
                        <Box className="pptx-sidebar__analysis-bar pptx-sidebar__analysis-bar--3">
                          <Box className="pptx-sidebar__analysis-fill" />
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Step 3 — Validated data (merged). Absorbs the work of
                      the previous "extract metrics" + "validate" + "quality
                      check" steps into a single beat. The detail panel
                      mixes data-validation rows with a quality-pass row
                      so the message is "data is sound AND the slide it
                      will produce passes our checks". */}
                  <Box className="pptx-sidebar__step pptx-sidebar__step--3">
                    <Box className="pptx-sidebar__step-row">
                      <Box className="pptx-sidebar__step-icon-stack">
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--spinner" />
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--check">&#10003;</span>
                      </Box>
                      <Box className="pptx-sidebar__step-label-stack">
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--active">Validating data...</span>
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--done">Validated data &middot; 92% confidence</span>
                      </Box>
                    </Box>
                    <Box className="pptx-sidebar__step-detail">
                      <Box className="pptx-sidebar__crossref-item pptx-sidebar__crossref-item--1">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>TAM validated (2 sources agree)
                      </Box>
                      <Box className="pptx-sidebar__crossref-item pptx-sidebar__crossref-item--2">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>Growth rates confirmed
                      </Box>
                      <Box className="pptx-sidebar__crossref-item pptx-sidebar__crossref-item--3">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>Quality check passed (font, contrast)
                      </Box>
                      <Box className="pptx-sidebar__crossref-item pptx-sidebar__crossref-item--4">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>Confidence: High (92%)
                      </Box>
                    </Box>
                  </Box>

                  {/* Step 5 — Building slide */}
                  <Box className="pptx-sidebar__step pptx-sidebar__step--5">
                    <Box className="pptx-sidebar__step-row">
                      <Box className="pptx-sidebar__step-icon-stack">
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--spinner" />
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--check">&#10003;</span>
                      </Box>
                      <Box className="pptx-sidebar__step-label-stack">
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--active">Building slide...</span>
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--done">Slide built &middot; 4 citations</span>
                      </Box>
                    </Box>
                    <Box className="pptx-sidebar__step-detail">
                      <Box className="pptx-sidebar__build-item pptx-sidebar__build-item--1">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>Layout template applied
                      </Box>
                      <Box className="pptx-sidebar__build-item pptx-sidebar__build-item--2">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>Chart rendered
                      </Box>
                      <Box className="pptx-sidebar__build-item pptx-sidebar__build-item--3">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>Source citations added
                      </Box>
                      <Box className="pptx-sidebar__build-item pptx-sidebar__build-item--4">
                        <span className="pptx-sidebar__build-spinner">&#9679;</span>Formatting...
                      </Box>
                    </Box>
                  </Box>

                  {/* Step 4 — Quality check (after slide is built, before drag) */}
                  <Box className="pptx-sidebar__step pptx-sidebar__step--4">
                    <Box className="pptx-sidebar__step-row">
                      <Box className="pptx-sidebar__step-icon-stack">
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--spinner" />
                        <span className="pptx-sidebar__step-icon pptx-sidebar__step-icon--check">&#10003;</span>
                      </Box>
                      <Box className="pptx-sidebar__step-label-stack">
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--active">Running quality check...</span>
                        <span className="pptx-sidebar__step-label pptx-sidebar__step-label--done">Quality check passed</span>
                      </Box>
                    </Box>
                    <Box className="pptx-sidebar__step-detail">
                      <Box className="pptx-sidebar__quality-item pptx-sidebar__quality-item--1">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>Font sizes ≥ 14pt
                      </Box>
                      <Box className="pptx-sidebar__quality-item pptx-sidebar__quality-item--2">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>Color contrast OK
                      </Box>
                      <Box className="pptx-sidebar__quality-item pptx-sidebar__quality-item--3">
                        <span className="pptx-sidebar__crossref-check">&#10003;</span>All sources cited
                      </Box>
                    </Box>
                  </Box>

                  {/* Chart drag happens visually at 80-85% after quality
                      check completes — no chat card needed for it. */}
                </Box>
              </Box>
            </Box>
            {/* Status bar */}
            <Box className="landing-hero__pptx-statusbar">
              <span>Slide 4 of 12 &nbsp; English (United States)</span>
              <Box className="landing-hero__pptx-statusbar-right">
                <Box className="landing-hero__pptx-statusbar-views">
                  <span className="landing-hero__pptx-statusbar-view" />
                  <span className="landing-hero__pptx-statusbar-view" />
                  <span className="landing-hero__pptx-statusbar-view landing-hero__pptx-statusbar-view--active" />
                </Box>
                <Box className="landing-hero__pptx-zoom-track">
                  <Box className="landing-hero__pptx-zoom-fill" />
                </Box>
                <span className="landing-hero__pptx-zoom-label">90%</span>
              </Box>
            </Box>
          </Box>
        </Box>
    </>
  );
}

export default OfficeHeroStage;
